import * as React from 'react';
import {
  PopoverContent,
  SearchableList,
  SearchableListBox,
  SearchableListInput,
  Divider,
  View,
  SearchDefaultIcon,
  Popover,
  PopoverTrigger,
  Button,
  AddIcon,
  SearchableListItem,
  SearchableListItemBody,
  SelectTrigger,
  type OptionType,
  type RowItemProps,
} from '@packages/systems/spring';
import {SEARCHABLE_LIST_BOX_VIRTUALIZED_MAX_HEIGHT} from '@packages/systems/design-system/constants/DesignerUI';

type SectionOption = {
  id: string;
  name: string;
  /**
   * Optional row treatment, e.g. `green` for component instances.
   */
  colorVariant?: RowItemProps['colorVariant'];
  /**
   * Optional row icon, shown in the list and on the closed selector button.
   */
  prefix?: RowItemProps['prefix'];
};

// Helper function to create stable options comparison
const useStableOptions = (options: SectionOption[]) => {
  const previousOptions = React.useRef<SectionOption[]>([]);

  const hasChanged = React.useMemo(() => {
    if (options.length !== previousOptions.current.length) {
      return true;
    }

    return options.some((option, index) => {
      const prevOption = previousOptions.current[index];
      return (
        !prevOption ||
        option.id !== prevOption.id ||
        option.name !== prevOption.name ||
        option.colorVariant !== prevOption.colorVariant ||
        option.prefix !== prevOption.prefix
      );
    });
  }, [options]);

  if (hasChanged) {
    previousOptions.current = options;
  }

  return previousOptions.current;
};

type SectionSelectorPopoverProps = {
  /**
   * The currently selected section value.
   */
  value: string | null | undefined;
  /**
   * List of available section options to select from.
   */
  options: SectionOption[];
  /**
   * Callback function to handle section selection.
   */
  onSectionChange: (domId: string | null) => void;
  /**
   * Text shown when no option is selected.
   */
  placeholder?: string;
  /**
   * Placeholder text for the search input inside the popover.
   */
  searchPlaceholder?: string;
  /**
   * Text shown when the list has no matching options.
   */
  emptyStateLabel?: string;
  /**
   * Optional action shown below the empty list.
   */
  emptyAction?: {
    label: string;
    onClick: () => void;
  };
  /**
   * Whether the options contain a valid selectable target.
   */
  hasValidOptions?: boolean;
};

/**
 * Renders a SearchableList within a popover for selecting a section.
 */
export const SectionSelectorPopover = ({
  value,
  options,
  onSectionChange,
  placeholder = 'Choose a page section...',
  searchPlaceholder = 'Search sections',
  emptyStateLabel,
  emptyAction,
  hasValidOptions = options.length > 0,
}: SectionSelectorPopoverProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = options.find((section) => section.id === value);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      width="300px"
      minHeight="fit-content"
      // needs to escape the viewport or it will be cut off when rendered in the right sidebar
      shouldEscapeViewport
    >
      <PopoverTrigger>
        <SelectTrigger
          data-automation-id="section-selector-button"
          prefix={selectedOption?.prefix}
          id="section-selector-button"
          isOpen={isOpen}
          onClick={() => setIsOpen((prev) => !prev)}
          shouldTruncateButtonText
        >
          {selectedOption?.name || placeholder}
        </SelectTrigger>
      </PopoverTrigger>
      <SectionSelectorContent
        value={value}
        options={options}
        searchPlaceholder={searchPlaceholder}
        emptyStateLabel={emptyStateLabel}
        emptyAction={emptyAction}
        hasValidOptions={hasValidOptions}
        onClose={() => setIsOpen(false)}
        onSectionChange={(val) => {
          onSectionChange(val);
          setIsOpen(false);
        }}
      />
    </Popover>
  );
};

const SectionSelectorContent = ({
  value,
  options,
  onSectionChange,
  searchPlaceholder = 'Search sections',
  emptyStateLabel,
  emptyAction,
  hasValidOptions,
  onClose,
}: Omit<SectionSelectorPopoverProps, 'hasValidOptions'> & {
  hasValidOptions: boolean;
  onClose: () => void;
}) => {
  // Use stable options to prevent unnecessary re-renders
  const stableOptions = useStableOptions(options);

  const memoizedOptions = React.useMemo(() => {
    return stableOptions.map((option) => ({
      label: option.name,
      value: option.id,
      colorVariant: option.colorVariant,
      prefix: option.prefix,
    }));
  }, [stableOptions]);

  const renderOption = React.useCallback(
    (option: OptionType & Pick<SectionOption, 'colorVariant'>) => (
      <SearchableListItem
        colorVariant={option.colorVariant}
        prefix={option.prefix}
        data-automation-id={`section-selector-option-${option.value}`}
      >
        <SearchableListItemBody>{option.label}</SearchableListItemBody>
      </SearchableListItem>
    ),
    []
  );

  const renderEmptyStateLabel = React.useMemo(
    () => (emptyStateLabel ? () => emptyStateLabel : undefined),
    [emptyStateLabel]
  );

  return (
    <PopoverContent
      isSeamless
      data-automation-id="section-selector-popover"
      colorVariant="tertiary"
    >
      <SearchableList
        checkmarkStyle="icon"
        onChange={(val) => {
          // If the selected value is the same as the current value, deselect it
          // Otherwise, select the new value
          onSectionChange(val === value ? null : val);
        }}
        options={memoizedOptions}
        renderOption={renderOption}
        renderEmptyStateLabel={renderEmptyStateLabel}
        value={value || undefined}
        selectionMode="single"
      >
        <View padding="$2" borderBottom="$border1">
          <SearchableListInput
            placeholder={searchPlaceholder}
            prefix={<SearchDefaultIcon />}
            data-automation-id="section-selector-search"
          />
        </View>
        <SearchableListBox
          isVirtualized
          maxHeight={SEARCHABLE_LIST_BOX_VIRTUALIZED_MAX_HEIGHT}
          hasGroupDividers
        />
      </SearchableList>
      <Divider />
      {emptyAction && !hasValidOptions ? (
        <Button
          data-automation-id="section-selector-empty-action"
          prefix={AddIcon}
          variant="ghost"
          isFullWidth
          justify="start"
          onClick={() => {
            onClose();
            emptyAction.onClick();
          }}
        >
          {emptyAction.label}
        </Button>
      ) : null}
    </PopoverContent>
  );
};
